import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Observable, Subject, catchError, debounceTime, filter, forkJoin, map, of, switchMap, takeUntil, tap } from 'rxjs';

import { FormBuilderService } from '../service/form-builder.service';
import { MarketingFormsService, CatalogOption } from '../service/marketing-forms.service';
import { TemplateService } from '../service/template.service';
import { ToastService } from '../../../shared/services/toast.service';
import { FieldPaletteComponent } from '../components/editor-panel/field-palette/field-palette.component';
import { FormCanvasComponent, PaletteDropEvent } from '../components/editor-panel/form-canvas/form-canvas.component';
import { FieldPropertiesComponent } from '../components/editor-panel/field-properties/field-properties.component';
import { FormPreviewComponent } from '../components/editor-panel/form-preview/form-preview.component';
import { FormSettingsPanelComponent } from '../components/editor-panel/form-settings/form-settings-panel.component';
import { FormTargetUrlsPanelComponent } from '../components/editor-panel/form-target-urls/form-target-urls-panel.component';
import { FormCrmMappingPanelComponent, FieldCrmMapPair } from '../components/editor-panel/form-crm-mapping/form-crm-mapping-panel.component';
import { FormStatusPipe } from '../pipes/form-status.pipe';

import { FieldType, FormFieldCrmMap, FormFieldModel, FormFieldOption } from '../models/form-field.model';
import { FIELD_CAPS } from '../utils/field-caps.util';
import { FormSettingModel } from '../models/form.model';
import { MktFormTargetUrl, MktFormTargetUrlRequest } from '../models/target-url.model';
import { buildPublicFormUrl } from '../utils/public-form-url';
import { extractApiErrorMessage } from '../utils/api-error.util';

const FIELD_LABELS: Record<FieldType, string> = {
  TEXT: 'Texto corto',
  TEXTAREA: 'Texto largo',
  EMAIL: 'Correo electrónico',
  PHONE: 'Teléfono',
  NUMBER: 'Número',
  DATE: 'Fecha',
  DROPDOWN: 'Desplegable',
  CHECKBOXES: 'Checkboxes',
  RADIO: 'Opción única',
  FILE_UPLOAD: 'Subir archivo',
  GDPR_CONSENT: 'Acepto los términos y condiciones',
  HIDDEN: 'Campo oculto',
  RATING_NPS: 'NPS / Rating',
  GEO_COUNTRY_REGION: 'País / región',
};

type EditorTab = 'fields' | 'settings' | 'urls' | 'crm' | 'share';

@Component({
  selector: 'app-form-editor',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    FieldPaletteComponent,
    FormCanvasComponent,
    FieldPropertiesComponent,
    FormPreviewComponent,
    FormSettingsPanelComponent,
    FormTargetUrlsPanelComponent,
    FormCrmMappingPanelComponent,
    FormStatusPipe,
  ],
  providers: [FormBuilderService],
  templateUrl: './form-editor.component.html',
})
export class FormEditorComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly styleChanges$ = new Subject<Partial<FormSettingModel>>();

  formId!: number;
  activeTab: EditorTab = 'fields';
  isLoading = true;
  isSaving = false;
  isPublishing = false;
  lastSavedAt: Date | null = null;
  saveError: string | null = null;
  previewOpen = false;
  showTemplateModal = false;
  isSavingTemplate = false;
  templateName = '';
  saveDropdownOpen = false;

  @HostListener('document:click')
  onDocumentClick(): void {
    this.saveDropdownOpen = false;
  }

  // Settings
  settings: FormSettingModel | null = null;
  settingsLoading = false;
  settingsSaving = false;
  private settingsAttempted = false;

  // Target URLs
  targetUrls: MktFormTargetUrl[] = [];
  urlsLoading = false;
  urlsSaving = false;

  // CRM Mapping (resumen de solo lectura; la edición vive en field-properties)
  crmRows: FieldCrmMapPair[] = [];
  crmLoading = false;

  private readonly allTabs: { id: EditorTab; label: string }[] = [
    { id: 'fields',   label: 'Campos' },
    { id: 'settings', label: 'Configuración' },
    { id: 'urls',     label: 'URLs destino' },
    { id: 'crm',      label: 'Mapeo CRM' },
    { id: 'share',    label: 'Distribución' },
  ];

  get tabs(): { id: EditorTab; label: string }[] {
    return this.isTemplate
      ? this.allTabs.filter(t => t.id !== 'share')
      : this.allTabs;
  }

  get isTemplate(): boolean {
    return this.state.form?.isTemplate ?? false;
  }

  private originalFieldIds = new Set<number>();
  // Snapshot del estado servidor al cargar fields: options y crm-map por field
  private originalOptionsByFieldId = new Map<number, FormFieldOption[]>();
  private originalCrmMapByFieldId = new Map<number, FormFieldCrmMap | null>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    readonly builder: FormBuilderService,
    private formsService: MarketingFormsService,
    private templateService: TemplateService,
    private toast: ToastService,
  ) {}

  ngOnInit(): void {
    this.formId = Number(this.route.snapshot.paramMap.get('id'));
    this.loadForm();
    this.setupAutosave();
    this.styleChanges$.pipe(
      debounceTime(800),
      takeUntil(this.destroy$),
    ).subscribe(partial => this.saveStyleSilent(partial));
  }

  ngOnDestroy(): void {
    this.builder.reset();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Tabs ──────────────────────────────────────────────────────────────────

  setTab(tab: EditorTab): void {
    this.activeTab = tab;
    switch (tab) {
      case 'settings': this.loadSettings(); break;
      case 'urls': this.loadTargetUrls(); break;
      case 'crm': this.loadCrmRows(); break;
    }
  }

  // ── Acciones de Estado ────────────────────────────────────────────────────

  onPublish(): void {
    if (this.isPublishing) return;

    // Si hay cambios sin guardar, avisamos que debe guardar primero
    if (this.builder.snapshot.isDirty) {
      this.toast.warning('Guarda los cambios antes de publicar');
      return;
    }

    this.isPublishing = true;
    this.formsService.publishForm(this.formId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (form) => {
          this.builder.loadForm(form, this.builder.snapshot.fields, this.settings);
          this.isPublishing = false;
          this.toast.success('Formulario publicado');
        },
        error: err => {
          this.isPublishing = false;
          this.toast.error(extractApiErrorMessage(err, 'No se pudo publicar el formulario'));
        },
      });
  }

  onPause(): void {
    if (this.isPublishing) return;
    this.isPublishing = true;
    this.formsService.pauseForm(this.formId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (form) => {
          this.builder.loadForm(form, this.builder.snapshot.fields, this.settings);
          this.isPublishing = false;
          this.toast.success('Formulario pausado');
        },
        error: err => {
          this.isPublishing = false;
          this.toast.error(extractApiErrorMessage(err, 'No se pudo pausar el formulario'));
        },
      });
  }

  get canPublish(): boolean {
    const status = this.state.form?.status;
    return status === 'DRAFT' || status === 'PAUSED' || status === 'ARCHIVED';
  }

  get canPause(): boolean {
    return this.state.form?.status === 'PUBLISHED';
  }

  getPublicUrl(): string {
    return buildPublicFormUrl(this.state.form?.slug);
  }

  copyPublicUrl(): void {
    const url = this.getPublicUrl();
    if (!url) return;

    navigator.clipboard.writeText(url).then(() => {
      this.toast.success('Enlace copiado al portapapeles');
    }).catch(() => {
      this.toast.error('No se pudo copiar el enlace');
    });
  }

  // ── Autosave ──────────────────────────────────────────────────────────────

  private setupAutosave(): void {
    this.builder.state.pipe(
      takeUntil(this.destroy$),
      filter(s => s.isDirty && !s.isLoading),
      debounceTime(2_000),
    ).subscribe(() => this.save(true));
  }

  // ── Carga inicial ─────────────────────────────────────────────────────────

  private loadForm(): void {
    this.isLoading = true;
    // Marcar antes del forkJoin para evitar que un cambio de tab concurrente
    // dispare loadSettings() mientras esta carga ya está en vuelo.
    this.settingsAttempted = true;
    forkJoin([
      this.formsService.getFormById(this.formId),
      this.formsService.getFields(this.formId),
      // Settings se crean automáticamente al crear el form, así que existen siempre.
      // Si falla por alguna razón, igualmente arrancamos sin ellos.
      this.formsService.getSettings(this.formId).pipe(catchError(() => of(null))),
    ]).pipe(takeUntil(this.destroy$)).subscribe({
      next: ([form, fields, settings]) => {
        const hydrate$ = fields.length
          ? forkJoin(fields.map(f => this.hydrateField(f)))
          : of(fields);
        hydrate$.pipe(takeUntil(this.destroy$)).subscribe({
          next: hydratedFields => {
            this.builder.loadForm(form, hydratedFields, settings);
            this.settings = settings;
            this.originalFieldIds = new Set(hydratedFields.filter(x => x.id).map(x => x.id!));
            this.isLoading = false;
          },
          error: () => this.handleLoadError(),
        });
      },
      error: () => this.handleLoadError(),
    });
  }

  private hydrateField(field: FormFieldModel): Observable<FormFieldModel> {
    if (!field.id || !this.fieldNeedsOptions(field.fieldType)) {
      this.originalOptionsByFieldId.set(field.id ?? -1, []);
      return of(field);
    }
    return this.formsService.getFieldOptions(this.formId, field.id).pipe(
      map(opts => {
        const options = opts ?? [];
        this.originalOptionsByFieldId.set(field.id!, this.cloneOptions(options));
        return { ...field, options };
      }),
    );
  }

  private fieldNeedsOptions(type: FieldType): boolean {
    return type === 'DROPDOWN' || type === 'RADIO' || type === 'CHECKBOXES';
  }

  private handleLoadError(): void {
    this.toast.error('No se pudo cargar el formulario');
    this.router.navigate(['/marketing-forms']);
  }

  // ── Settings ──────────────────────────────────────────────────────────────

  private loadSettings(): void {
    // Already loaded (or attempted) in loadForm — avoid a redundant request.
    if (this.settingsAttempted || this.settingsLoading) return;
    this.settingsLoading = true;
    this.formsService.getSettings(this.formId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: s => {
          this.settings = s;
          this.builder.setSettings(s);
          this.settingsAttempted = true;
          this.settingsLoading = false;
        },
        error: () => {
          // 404 means settings don't exist yet (normal for forms created before
          // the auto-create logic). Treat as null so the save flow uses POST.
          this.settingsAttempted = true;
          this.settingsLoading = false;
        },
      });
  }

  onSaveSettings(payload: Partial<FormSettingModel>): void {
    this.settingsSaving = true;
    this.formsService.updateSettings(this.formId, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: s => {
          this.settings = s;
          this.builder.setSettings(s);
          this.settingsSaving = false;
          this.toast.success('Configuración guardada');
        },
        error: (err) => {
          this.settingsSaving = false;
          this.toast.error(extractApiErrorMessage(err, 'No se pudo guardar la configuración.'));
        },
      });
  }

  // ── Target URLs ───────────────────────────────────────────────────────────

  private loadTargetUrls(): void {
    this.urlsLoading = true;
    this.formsService.getTargetUrls(this.formId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: urls => { this.targetUrls = urls ?? []; this.urlsLoading = false; },
        error: err => {
          this.toast.error(extractApiErrorMessage(err, 'No se pudieron cargar las URLs destino'));
          this.urlsLoading = false;
        },
      });
  }

  onCreateUrl(payload: MktFormTargetUrlRequest): void {
    this.urlsSaving = true;
    this.formsService.createTargetUrl(this.formId, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => { this.urlsSaving = false; this.toast.created('Regla'); this.loadTargetUrls(); },
        error: err => { this.urlsSaving = false; this.toast.error(extractApiErrorMessage(err, 'No se pudo crear la regla')); },
      });
  }

  onUpdateUrl(event: { id: number; payload: MktFormTargetUrlRequest }): void {
    this.urlsSaving = true;
    this.formsService.updateTargetUrl(this.formId, event.id, event.payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => { this.urlsSaving = false; this.toast.success('Regla actualizada'); this.loadTargetUrls(); },
        error: err => { this.urlsSaving = false; this.toast.error(extractApiErrorMessage(err, 'No se pudo actualizar la regla')); },
      });
  }

  onDeleteUrl(id: number): void {
    this.formsService.deleteTargetUrl(this.formId, id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => { this.toast.deleted('Regla'); this.loadTargetUrls(); },
        error: err => this.toast.error(extractApiErrorMessage(err, 'No se pudo borrar la regla')),
      });
  }

  // ── CRM Mapping ───────────────────────────────────────────────────────────

  private loadCrmRows(): void {
    const fields = this.builder.snapshot.fields.filter(f => !!f.id);
    if (!fields.length) {
      this.crmRows = [];
      return;
    }
    this.crmLoading = true;
    const requests = fields.map(f =>
      this.formsService.getFieldCrmMap(this.formId, f.id!).pipe(
        catchError(() => of(null)),
      ),
    );
    forkJoin(requests)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: mappings => {
          this.crmRows = fields.map((f, i) => ({ field: f, mapping: mappings[i] ?? null }));
          this.crmLoading = false;
        },
        error: () => {
          this.crmRows = fields.map(f => ({ field: f, mapping: null }));
          this.crmLoading = false;
        },
      });
  }

  onMappingChanged(event: { fieldId: number; mapping: FormFieldCrmMap | null }): void {
    this.crmRows = this.crmRows.map(row =>
      row.field.id === event.fieldId ? { ...row, mapping: event.mapping } : row,
    );
  }

  onFixedValueChanged(event: { fieldId: number; value: string }): void {
    const field = this.builder.snapshot.fields.find(f => f.id === event.fieldId);
    if (field?.fieldKey) {
      this.builder.updateField(field.fieldKey, { defaultFixedValue: event.value });
    }
  }

  onCatalogOptionsSync(event: { fieldId: number; options: FormFieldOption[] }): void {
    const field = this.builder.snapshot.fields.find(f => f.id === event.fieldId);
    if (!field?.fieldKey) return;
    this.builder.updateField(field.fieldKey, { options: event.options });
    this.builder.catalogSynced$.next({ fieldKey: field.fieldKey, options: event.options });
  }

  // ── Edición de campos ─────────────────────────────────────────────────────

  onFieldDropped(event: PaletteDropEvent): void {
    if (FIELD_CAPS[event.type].singleton && this.usedFieldTypes.has(event.type)) {
      this.toast.warning(`Solo puede haber un campo de tipo "${FIELD_LABELS[event.type]}" por formulario`);
      return;
    }
    const newField: FormFieldModel = {
      formId: this.formId,
      fieldKey: `${event.type.toLowerCase()}_${Date.now()}`,
      fieldType: event.type,
      label: FIELD_LABELS[event.type],
      isRequired: false,
      isHidden: event.type === 'HIDDEN',
      isSmartField: false,
      isProgressive: false,
      sortOrder: event.atIndex,
      columnSpan: 3,
      stepNumber: this.builder.snapshot.activeStep,
      options: this.fieldNeedsOptions(event.type)
        ? [{ optionLabel: 'Opción 1', optionValue: 'opcion_1', sortOrder: 0, isDefault: true }]
        : undefined,
    };
    this.builder.insertField(newField, event.atIndex);
    this.builder.selectField(newField.fieldKey);
  }

  onRemoveField(fieldKey: string): void {
    this.builder.removeField(fieldKey);
  }

  // ── Guardado de campos + options ──────────────────────────────────────────

  save(silent = false): void {
    if (!this.builder.snapshot.form || this.isSaving) return;
    this.isSaving = true;
    this.saveError = null;
    this.buildSavePipeline().pipe(takeUntil(this.destroy$)).subscribe({
      next: saved => this.applySaveResult(saved, silent),
      error: (err: unknown) => {
        this.isSaving = false;
        const msg = extractApiErrorMessage(err, 'Error al guardar el formulario');
        const details = (err as any)?.error?.details;
        this.saveError = msg;
        // Always show the error, silent or not, so the user knows what happened
        this.toast.error(details ? `${msg}: ${details}` : msg);
        if (silent) {
          // Re-mark as dirty so the autosave retries on the next interaction
          this.builder.markDirty();
        }
      },
    });
  }

  private buildSavePipeline(): Observable<FormFieldModel[]> {
    const fields = this.builder.snapshot.fields;
    const toDeleteIds = [...this.originalFieldIds].filter(id => !fields.some(f => f.id === id));
    const persist$ = this.persistAllFields(fields);
    return toDeleteIds.length
      ? forkJoin(toDeleteIds.map(id => this.formsService.deleteField(this.formId, id))).pipe(
          switchMap(() => persist$),
        )
      : persist$;
  }

  private applySaveResult(savedFields: FormFieldModel[], silent: boolean): void {
    // Calcular antes de actualizar originalFieldIds para poder comparar.
    const savedIds = new Set(savedFields.filter(f => f.id).map(f => f.id!));
    const structureChanged =
      savedIds.size !== this.originalFieldIds.size ||
      [...savedIds].some(id => !this.originalFieldIds.has(id));

    this.builder.markSaved();
    this.builder.patchFieldIds(savedFields);
    this.originalFieldIds = savedIds;
    this.isSaving = false;
    this.saveError = null;
    this.lastSavedAt = new Date();
    if (!silent) this.toast.success('Formulario guardado');

    if (structureChanged && (this.crmRows.length || this.activeTab === 'crm')) {
      // Campos añadidos o eliminados → re-fetch completo para que el tab CRM
      // muestre/oculte filas correctamente.
      this.loadCrmRows();
    } else if (this.crmRows.length) {
      // Solo cambiaron propiedades (label, placeholder, etc.) — los mappings CRM
      // no cambian con el save principal. Actualizar refs de campo desde el estado
      // del builder sin disparar requests adicionales.
      const snapshot = this.builder.snapshot.fields;
      this.crmRows = this.crmRows.map(row => ({
        ...row,
        field: snapshot.find(f => f.fieldKey === row.field.fieldKey) ?? row.field,
      }));
    }
  }

  // Crea/actualiza cada field y luego sincroniza sus options.
  private persistAllFields(fields: FormFieldModel[]): Observable<FormFieldModel[]> {
    if (!fields.length) return of([]);
    // sortOrder relativo al paso: cada paso numera desde 0 para no entremezclar
    // el orden entre pasos distintos al persistir (inc34).
    const stepCounters = new Map<number, number>();
    const ops = fields.map(f => {
      const step = f.stepNumber ?? 1;
      const sortOrder = stepCounters.get(step) ?? 0;
      stepCounters.set(step, sortOrder + 1);
      const payload: FormFieldModel = { ...f, sortOrder };
      const save$ = f.id
        ? this.formsService.updateField(this.formId, f.id, payload)
        : this.formsService.createField(this.formId, payload);
      return save$.pipe(
        switchMap(saved => this.syncOptions(saved, f.options ?? []).pipe(
          switchMap(syncedOptions => of({ ...saved, options: syncedOptions } as FormFieldModel)),
        )),
      );
    });
    return forkJoin(ops);
  }

  // Diff entre options locales (deseado) y options originales del servidor.
  // - Crea las que no tienen id
  // - Actualiza las que existen y cambiaron
  // - Borra las que existían en el servidor pero ya no están localmente
  private syncOptions(field: FormFieldModel, desired: FormFieldOption[]): Observable<FormFieldOption[]> {
    if (!field.id) return of([]);
    if (!this.fieldNeedsOptions(field.fieldType)) return of([]);

    const original = this.originalOptionsByFieldId.get(field.id) ?? [];
    const desiredIds = new Set(desired.filter(o => o.id).map(o => o.id!));
    const toDelete = original.filter(o => o.id && !desiredIds.has(o.id));

    const upsertOps: Observable<FormFieldOption>[] = desired.map((opt, idx) => {
      const payload: Partial<FormFieldOption> = {
        optionLabel: opt.optionLabel,
        optionValue: opt.optionValue,
        sortOrder: idx,
        isDefault: !!opt.isDefault,
        isActive: opt.isActive !== false,
        crmEnumId: opt.crmEnumId,
      };
      return opt.id
        ? this.formsService.updateFieldOption(this.formId, field.id!, opt.id, payload)
        : this.formsService.createFieldOption(this.formId, field.id!, payload);
    });

    // Deletes run first so there is no race condition between concurrent
    // DELETE and INSERT on the same (form_field_id, option_value) pair.
    const delete$ = toDelete.length
      ? forkJoin(toDelete.map(o => this.formsService.deleteFieldOption(this.formId, field.id!, o.id!)))
      : of([] as unknown[]);

    return delete$.pipe(
      switchMap(() => upsertOps.length ? forkJoin(upsertOps) : of([])),
      switchMap(synced => {
        this.originalOptionsByFieldId.set(field.id!, this.cloneOptions(synced));
        return of(synced);
      }),
    );
  }

  private cloneOptions(opts: FormFieldOption[]): FormFieldOption[] {
    return opts.map(o => ({ ...o }));
  }

  onStyleSettingsChange(partial: Partial<FormSettingModel>): void {
    this.builder.setSettings({ ...(this.settings ?? {}), ...partial } as FormSettingModel);
    this.styleChanges$.next({ ...(this.settings ?? {}), ...partial });
  }

  private saveStyleSilent(payload: Partial<FormSettingModel>): void {
    this.formsService.updateSettings(this.formId, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: s => { this.settings = s; this.builder.setSettings(s); },
        error: err => {
          console.warn('[form-editor] saveStyleSilent falló — los estilos del servidor pueden estar desactualizados', err);
          this.toast.warning('No se pudieron guardar los estilos. Recarga la página si el problema persiste.');
        },
      });
  }

  openTemplateModal(): void {
    this.templateName = this.state.form?.name ?? '';
    this.showTemplateModal = true;
  }

  cancelTemplateModal(): void {
    this.showTemplateModal = false;
  }

  confirmSaveAsTemplate(): void {
    const name = this.templateName.trim();
    if (!name || this.isSavingTemplate) return;

    this.isSavingTemplate = true;

    // No pending changes AND no save in flight — server is up to date, clonar directo.
    if (!this.builder.snapshot.isDirty && !this.isSaving) {
      this.templateService.saveAsTemplate(this.formId, name)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.isSavingTemplate = false;
            this.showTemplateModal = false;
            this.toast.success(`Plantilla "${name}" guardada`);
          },
          error: err => {
            this.isSavingTemplate = false;
            this.toast.error(extractApiErrorMessage(err, 'No se pudo guardar la plantilla'));
          },
        });
      return;
    }

    // Pending changes — persist first, then clone as template in one pipeline.
    this.isSaving = true;
    this.buildSavePipeline().pipe(
      takeUntil(this.destroy$),
      tap(saved => this.applySaveResult(saved, true)),
      switchMap(() => this.templateService.saveAsTemplate(this.formId, name)),
    ).subscribe({
      next: () => {
        this.isSavingTemplate = false;
        this.showTemplateModal = false;
        this.toast.success(`Plantilla "${name}" guardada`);
      },
      error: err => {
        this.isSaving = false;
        this.isSavingTemplate = false;
        this.toast.error(extractApiErrorMessage(err, 'No se pudo guardar la plantilla'));
      },
    });
  }

  // Called by goBack() and the CanDeactivate guard.
  // Has NO takeUntil(destroy$) so the HTTP requests survive navigation/destroy.
  saveIfDirty(): Observable<boolean> {
    if (!this.builder.snapshot.isDirty || this.isSaving) return of(true);
    this.isSaving = true;
    return this.buildSavePipeline().pipe(
      tap(saved => this.applySaveResult(saved, true)),
      map(() => true as boolean),
      catchError(() => of(true as boolean)),
    );
  }

  goBack(): void {
    this.saveIfDirty().subscribe(() => this.router.navigate(['/marketing-forms']));
  }

  get state() {
    return this.builder.snapshot;
  }

  // Hay campos nuevos (sin id) o cambios sin guardar en la pestaña Campos.
  get hasUnsavedFields(): boolean {
    const s = this.builder.snapshot;
    return s.isDirty || s.fields.some(f => !f.id);
  }

  get usedFieldTypes(): Set<FieldType> {
    return new Set(this.builder.snapshot.fields.map(f => f.fieldType));
  }

  get formStatusBadgeClass(): string {
    switch (this.state.form?.status) {
      case 'PUBLISHED': return 'bg-success-100 text-success-700 dark:bg-success-500/15 dark:text-success-400';
      case 'DRAFT':     return 'bg-warning-100 text-warning-700 dark:bg-warning-500/15 dark:text-warning-400';
      case 'PAUSED':    return 'bg-error-100 text-error-700 dark:bg-error-500/15 dark:text-error-400';
      // `info` no es una familia del tema (las de estado son success/warning/error, más
      // blue-light para lo informativo): con `bg-info-100` el badge salía sin fondo ni color.
      case 'ARCHIVED':  return 'bg-blue-light-100 text-blue-light-700 dark:bg-blue-light-500/15 dark:text-blue-light-400';
      default:          return 'bg-gray-100 text-gray-600';
    }
  }
}
